import React from "react";
import PropTypes from "prop-types";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";

/**
 * 基礎匯出（主計劃：基礎匯出 csv/簡易 xlsx）。
 *
 * 第一階段先滿足「報表可匯出」；第二階段的高保真匯出引擎（凍結欄/樣式/
 * WYSIWYG PDF）另做，不在此元件範圍。
 *
 * columns: [{ key, label }]；rows: 物件陣列。
 */

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function cell(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

/**
 * 匯出 CSV。加 UTF-8 BOM 讓 Excel 正確辨識中文。
 */
export function exportCsv(rows, columns, filename = "export.csv") {
  const header = columns.map((c) => `"${c.label}"`).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => `"${cell(r[c.key]).replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\r\n");
  const csv = `﻿${header}\r\n${body}`;
  triggerDownload(new Blob([csv], { type: "text/csv;charset=utf-8;" }), filename);
}

/**
 * 匯出簡易 xlsx（實為 Excel 可開的 SpreadsheetML/HTML 表，無需額外套件）。
 * 高保真 .xlsx（凍結欄/樣式/篩選器）為第二階段引擎，屆時改用 SheetJS 等。
 */
export function exportXls(rows, columns, filename = "export.xls") {
  const esc = (v) =>
    cell(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const head = columns.map((c) => `<th>${esc(c.label)}</th>`).join("");
  const body = rows
    .map(
      (r) =>
        `<tr>${columns.map((c) => `<td>${esc(r[c.key])}</td>`).join("")}</tr>`
    )
    .join("");
  const html =
    `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head>` +
    `<meta charset="UTF-8"></head><body>` +
    `<table border="1"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>` +
    `</body></html>`;
  triggerDownload(
    new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" }),
    filename
  );
}

/**
 * 匯出按鈕（下拉 CSV / Excel）。
 *   <ExportButton rows={rows} columns={cols} filename="使用者清單" />
 */
export const ExportButton = ({
  rows,
  columns,
  filename = "export",
  disabled = false,
  label = "匯出",
}) => {
  const empty = !rows || rows.length === 0;
  return (
    <UncontrolledDropdown>
      <DropdownToggle color="light" caret disabled={disabled || empty}>
        <i className="mdi mdi-download me-1" />{label}
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem onClick={() => exportCsv(rows, columns, `${filename}.csv`)}>
          <i className="mdi mdi-file-delimited-outline me-2" />CSV
        </DropdownItem>
        <DropdownItem onClick={() => exportXls(rows, columns, `${filename}.xls`)}>
          <i className="mdi mdi-file-excel-outline me-2" />Excel（簡易）
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};
ExportButton.propTypes = {
  rows: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
  ),
  filename: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
};

export default ExportButton;
